/**
 * SuperAdminDashboard — Platform-level admin portal.
 * Role-gated: only admins with role='super_admin' can access.
 * Route: /superadmin
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/lib/store';
import { adminRequestAPI } from '@/lib/api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Shield, LayoutDashboard, Building2, CreditCard, Inbox, ScrollText, Sparkles, LogOut, ArrowLeft, Home, Settings2, LifeBuoy, Megaphone, TrendingUp, ShieldAlert, Users, UserPlus, Clock } from 'lucide-react';

import { DashboardTab } from '@/components/superadmin/DashboardTab';
import { TenantsTab } from '@/components/superadmin/TenantsTab';
import { PlansTab } from '@/components/superadmin/PlansTab';
import { ApplicationsTab } from '@/components/superadmin/ApplicationsTab';
import { UnassignedEnquiriesTab } from '@/components/superadmin/UnassignedEnquiriesTab';
import { BillingTab } from '@/components/superadmin/BillingTab';
import { AuditTab } from '@/components/superadmin/AuditTab';
import { PlatformSettingsTab } from '@/components/superadmin/PlatformSettingsTab';
import { SupportTab } from '@/components/superadmin/SupportTab';
import { BroadcastsTab } from '@/components/superadmin/BroadcastsTab';
import { AnalyticsTab } from '@/components/superadmin/AnalyticsTab';
import { ComplianceTab } from '@/components/superadmin/ComplianceTab';
import AdminUsersPanel from '@/components/superadmin/AdminUsersPanel';
import AdminRequestsPanel from '@/components/AdminRequestsPanel';
import EventModerationPanel from '@/components/EventModerationPanel';
import SecurityOfficersManagement from '@/components/SecurityOfficersManagement';
import ShiftManagement from '@/components/ShiftManagement';
import OrganizationManagement from '@/components/OrganizationManagement';

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') || 'dashboard');

  // Keep the URL in sync with the active tab so external links (e.g. legacy
  // `/admin?tab=admins` redirect) land on the right screen and refreshes stick.
  useEffect(() => {
    const current = searchParams.get('tab');
    if (current !== tab) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', tab);
      setSearchParams(next, { replace: true });
    }
     
  }, [tab]);

  // Pending Admin Requests count → badge on the Requests tab.
  const { data: pendingRequests } = useQuery({
    queryKey: ['superadmin-requests-pending'],
    queryFn: () => adminRequestAPI.list('pending').then((r) => r.data),
    enabled: !!user && (user.role === 'super_admin' || user.is_super_admin),
    refetchInterval: 60_000,
  });
  const pendingCount = pendingRequests?.length || 0;

  const role = user?.role;
  const isSuper = role === 'super_admin' || user?.is_super_admin;

  if (!isSuper) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="superadmin-gate">
        <div className="max-w-md w-full bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 text-center text-white">
          <Shield className="h-12 w-12 text-rose-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold">Access Denied</h2>
          <p className="text-slate-400 text-sm mt-2">
            This area is restricted to Platform Super Admins. Your role ({role || 'unknown'}) does not have access.
          </p>
          <div className="flex gap-3 justify-center mt-6">
            <Link to="/admin">
              <Button variant="outline" className="text-white border-white/20 bg-white/5 hover:bg-white/10">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back to Admin
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const TABS = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp },
    { id: 'compliance', label: 'Compliance', icon: ShieldAlert },
    { id: 'tenants', label: 'Tenants', icon: Building2 },
    { id: 'plans', label: 'Plans', icon: Sparkles },
    { id: 'applications', label: 'Applications', icon: Inbox },
    { id: 'enquiries', label: 'Membership Enquiries', icon: Inbox },
    { id: 'billing', label: 'Billing', icon: CreditCard },
    { id: 'support', label: 'Support', icon: LifeBuoy },
    { id: 'broadcasts', label: 'Broadcasts', icon: Megaphone },
    // ── Identity & Ops (merged from /admin, iter 438k Phase 1) ──
    { id: 'admins', label: 'Admin Users', icon: Users },
    { id: 'requests', label: 'Admin Requests', icon: UserPlus, badgeCount: pendingCount },
    { id: 'moderation', label: 'Moderation', icon: Shield },
    { id: 'security-officers', label: 'Security Officers', icon: Shield },
    { id: 'shifts', label: 'Shifts', icon: Clock },
    { id: 'organization', label: 'Organization', icon: Building2 },
    // ── Platform ──
    { id: 'settings', label: 'Settings', icon: Settings2 },
    { id: 'audit', label: 'Audit Log', icon: ScrollText },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950/40 to-slate-900 text-white" data-testid="superadmin-dashboard">
      {/* Top bar */}
      <header className="border-b border-white/10 bg-slate-950/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-600 flex items-center justify-center">
            <Shield className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1">
            <h1 className="text-lg font-bold leading-tight">Platform Admin</h1>
            <p className="text-xs text-slate-400">Super-Admin Control Center</p>
          </div>
          <Link to="/admin">
            <Button size="sm" variant="outline" className="text-white border-white/20 bg-white/5 hover:bg-white/10">
              <Home className="h-4 w-4 mr-1" /> Main Admin
            </Button>
          </Link>
          <Button size="sm" variant="outline" onClick={() => { logout(); navigate('/login'); }}
                  className="text-rose-300 border-rose-400/30 bg-rose-500/10 hover:bg-rose-500/20" data-testid="superadmin-logout">
            <LogOut className="h-4 w-4 mr-1" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <div className="w-full overflow-x-auto overflow-y-hidden pb-1 mb-6" data-testid="superadmin-tabs-scroll">
            <TabsList className="bg-white/5 border border-white/10 p-1 gap-1 inline-flex w-max min-w-full h-auto flex-nowrap justify-start">
              {TABS.map((t) => (
                <TabsTrigger
                  key={t.id}
                  value={t.id}
                  className="flex-shrink-0 data-[state=active]:bg-indigo-500 data-[state=active]:text-white text-slate-300"
                  data-testid={`superadmin-tab-${t.id}`}
                >
                  <t.icon className="h-4 w-4 mr-2" /> {t.label}
                  {t.badgeCount > 0 && (
                    <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5">
                      {t.badgeCount}
                    </span>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          <TabsContent value="dashboard"><DashboardTab /></TabsContent>
          <TabsContent value="analytics"><AnalyticsTab /></TabsContent>
          <TabsContent value="compliance"><ComplianceTab /></TabsContent>
          <TabsContent value="tenants"><TenantsTab /></TabsContent>
          <TabsContent value="plans"><PlansTab /></TabsContent>
          <TabsContent value="applications"><ApplicationsTab /></TabsContent>
          <TabsContent value="enquiries"><UnassignedEnquiriesTab /></TabsContent>
          <TabsContent value="billing"><BillingTab /></TabsContent>
          <TabsContent value="support"><SupportTab /></TabsContent>
          <TabsContent value="broadcasts"><BroadcastsTab /></TabsContent>
          <TabsContent value="admins"><AdminUsersPanel /></TabsContent>
          <TabsContent value="requests"><AdminRequestsPanel /></TabsContent>
          <TabsContent value="moderation"><EventModerationPanel /></TabsContent>
          <TabsContent value="security-officers"><SecurityOfficersManagement /></TabsContent>
          <TabsContent value="shifts"><ShiftManagement /></TabsContent>
          <TabsContent value="organization"><OrganizationManagement /></TabsContent>
          <TabsContent value="settings"><PlatformSettingsTab /></TabsContent>
          <TabsContent value="audit"><AuditTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default SuperAdminDashboard;
